import React from 'react'
import {Link} from 'react-router-dom'
import {useTheme} from '@/shared/theme/ThemeContext'
import {toAbsoluteUrl} from '@/shared/utils'

interface ThemeAwareLogoProps {
  /**
   * Link destination when logo is clicked
   */
  to?: string
  /**
   * CSS classes for the img element
   */
  className?: string
  /**
   * Alt text for the logo image
   */
  alt?: string
  /**
   * Light theme logo path (relative to /media/logos/)
   */
  lightLogo?: string
  /**
   * Dark theme logo path (relative to /media/logos/)
   * If not provided, falls back to lightLogo
   */
  darkLogo?: string
}

/**
 * Theme-aware logo component that switches between light and dark versions
 * based on the current theme setting.
 */
export const ThemeAwareLogo: React.FC<ThemeAwareLogoProps> = ({
  to = '/dashboard',
  className = 'h-50px',
  alt = 'Logo',
  lightLogo = '/media/logos/ob_web_logo.svg',
  darkLogo, // Will be '/media/logos/ob_web_logo_dark.svg' when created
}) => {
  const {theme} = useTheme()

  // Use dark logo if available and in dark mode, otherwise use light logo
  const logoPath = theme === 'dark' && darkLogo ? darkLogo : lightLogo

  const logoElement = <img alt={alt} src={toAbsoluteUrl(logoPath)} className={className} />

  // If no link destination, return just the image
  if (!to) {
    return logoElement
  }

  // Return linked logo
  return <Link to={to}>{logoElement}</Link>
}

/**
 * Pre-configured logo for the main header
 */
export const HeaderLogo: React.FC<{to?: string}> = ({to = '/dashboard'}) => (
  <ThemeAwareLogo
    to={to}
    className='h-50px'
    alt='OmicsBox Logo'
    lightLogo='/media/logos/ob_web_logo.svg'
    darkLogo='/media/logos/ob_web_logo_dark.svg' // TODO: Create this file
  />
)

/**
 * Pre-configured logo for the welcome page header
 */
export const WelcomeLogo: React.FC = () => (
  <ThemeAwareLogo
    to='/welcome'
    className='h-50px'
    alt='OmicsBox Logo'
    lightLogo='/media/logos/ob_web_logo.svg'
    darkLogo='/media/logos/ob_web_logo_dark.svg' // TODO: Create this file
  />
)

/**
 * Pre-configured mobile logo
 */
export const MobileLogo: React.FC<{to?: string}> = ({to = '/'}) => (
  <ThemeAwareLogo
    to={to}
    className='h-30px'
    alt='OmicsBox Logo'
    lightLogo='/media/logos/omicsbox-logo.svg'
    darkLogo='/media/logos/omicsbox-logo.svg' // This might work for both themes
  />
)
